import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Apartment,
  CheckCircle,
  Email,
  Visibility,
  VisibilityOff,
  Key,
  MarkEmailRead,
  Refresh,
  Save,
  Search,
  SettingsSuggest,
  Shield,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { adminApi } from '../../services/adminApi';

const DEFAULT_PROFILE_SETTINGS = {
  defaultHomeTab: 'search',
  autoReturnToSearchAfterQuickEmail: true,
  compactAccounts: true,
  inboxAutoRefresh: true,
  showExpiryAlerts: true,
  footerSenderName: '',
  footerCompanyName: '',
  footerAddress: '',
  footerEmail: '',
  footerPhone: '',
};

const HOME_TAB_OPTIONS = [
  { value: 'search', label: 'Carrier Search' },
  { value: 'email-compose', label: 'Compose Email' },
  { value: 'email-campaign', label: 'Campaign Planner' },
  { value: 'email-inbox', label: 'Unified Inbox' },
  { value: 'activity', label: 'Activity' },
];

const formatDate = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return date.toLocaleString();
};

const clampPercent = (value) => Math.max(0, Math.min(100, Number(value) || 0));

function MiniStat({ title, value, helper, icon, color }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(color, 0.14)} 0%, ${alpha(color, 0.04)} 100%)`,
        borderColor: alpha(color, 0.22),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {helper}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(color, 0.14),
            color,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function UserProfile({
  user,
  token,
  darkMode,
  onToggleDarkMode,
  onSettingsSaved,
  refreshSession,
}) {
  const [profileData, setProfileData] = useState(user || {});
  const [workspacePrefs, setWorkspacePrefs] = useState({});
  const [settings, setSettings] = useState(DEFAULT_PROFILE_SETTINGS);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
  });
  const [footerForm, setFooterForm] = useState({
    footerSenderName: '',
    footerCompanyName: '',
    footerAddress: '',
    footerEmail: '',
    footerPhone: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [refreshing, setRefreshing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [notice, setNotice] = useState({ type: '', message: '' });

  useEffect(() => {
    let active = true;

    if (!token) {
      setProfileData(user || {});
      return undefined;
    }

    Promise.allSettled([adminApi.getProfile(token), adminApi.loadPrefs(token)])
      .then((results) => {
        if (!active) return;
        const profile = results[0].status === 'fulfilled'
          ? (results[0].value?.profile || user || {})
          : (user || {});
        const prefs = results[1].status === 'fulfilled'
          ? (results[1].value?.prefs || {})
          : {};

        setProfileData(profile);
        setWorkspacePrefs(prefs);
      });

    return () => {
      active = false;
    };
  }, [token, user]);

  useEffect(() => {
    const mergedSettings = {
      ...DEFAULT_PROFILE_SETTINGS,
      ...(workspacePrefs?.profileSettings || {}),
      ...(profileData?.profile_settings || {}),
      compactAccounts: typeof workspacePrefs?.compactAccounts === 'boolean'
        ? workspacePrefs.compactAccounts
        : DEFAULT_PROFILE_SETTINGS.compactAccounts,
    };

    setSettings(mergedSettings);
    setProfileForm({
      first_name: profileData?.first_name || '',
      last_name: profileData?.last_name || '',
      company_name: profileData?.company_name || '',
    });
    setFooterForm({
      footerSenderName: mergedSettings.footerSenderName || profileData?.name || '',
      footerCompanyName: mergedSettings.footerCompanyName || profileData?.company_name || '',
      footerAddress: mergedSettings.footerAddress || '',
      footerEmail: mergedSettings.footerEmail || profileData?.email || '',
      footerPhone: mergedSettings.footerPhone || '',
    });
  }, [profileData, workspacePrefs]);

  const usage = useMemo(() => {
    const searchLimit = Number(profileData?.quota_per_day) || 0;
    const emailLimit = Number(profileData?.email_daily_limit) || 0;
    const searches = Number(profileData?.searches_used_today) || 0;
    const emails = Number(profileData?.emails_sent_today) || 0;
    const aiEmails = Number(profileData?.ai_emails_generated_today) || 0;
    const smtpCount = Number(profileData?.smtp_accounts_count) || 0;
    const smtpLimit = Number(profileData?.smtp_accounts_limit) || 0;
    return {
      searches,
      emails,
      aiEmails,
      smtpCount,
      smtpLimit,
      totalSentMessages: Number(profileData?.sent_messages_count) || 0,
      totalOpened: Number(profileData?.tracking_opened) || 0,
      totalClicked: Number(profileData?.tracking_clicked) || 0,
      totalReplied: Number(profileData?.tracking_replied) || 0,
      searchLimit,
      emailLimit,
      searchPercent: searchLimit > 0 ? clampPercent((searches / searchLimit) * 100) : 0,
      emailPercent: emailLimit > 0 ? clampPercent((emails / emailLimit) * 100) : 0,
    };
  }, [profileData]);

  const subscriptionStatus = useMemo(() => {
    const days = Number(profileData?.subscription_days_remaining) || 0;
    const raw = String(profileData?.subscription_status || 'inactive').toLowerCase();
    const isExpired = Boolean(profileData?.subscription_is_expired);
    const status = isExpired ? 'expired' : raw;
    return {
      label:
        status === 'active' ? 'Active' :
        status === 'trial' ? 'Trial' :
        status === 'expired' ? 'Expired' :
        'Inactive',
      color:
        status === 'active' ? 'success' :
        status === 'trial' ? 'warning' :
        status === 'expired' ? 'error' :
        'default',
      days,
    };
  }, [profileData]);

  const fullName = [profileData?.first_name, profileData?.last_name].filter(Boolean).join(' ').trim()
    || profileData?.name
    || profileData?.email?.split('@')?.[0]
    || 'User';

  const handleRefresh = async () => {
    if (!token) return;
    setRefreshing(true);
    setNotice({ type: '', message: '' });
    try {
      await refreshSession?.(true);
      const [profileRes, prefsRes] = await Promise.all([
        adminApi.getProfile(token),
        adminApi.loadPrefs(token),
      ]);
      setProfileData(profileRes?.profile || {});
      setWorkspacePrefs(prefsRes?.prefs || {});
      setNotice({ type: 'success', message: 'Profile refreshed.' });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || 'Failed to refresh profile.' });
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!token) return;
    setSavingProfile(true);
    setNotice({ type: '', message: '' });
    try {
      const nextProfileSettings = {
        ...settings,
        ...footerForm,
      };

      const profileRes = await adminApi.updateProfile({
        profile: profileForm,
        profile_settings: nextProfileSettings,
      }, token);

      const nextPrefs = {
        ...workspacePrefs,
        compactAccounts: settings.compactAccounts,
        profileSettings: nextProfileSettings,
      };
      await adminApi.savePrefs(nextPrefs, token);

      setWorkspacePrefs(nextPrefs);
      setProfileData(profileRes?.profile || {});
      onSettingsSaved?.(nextProfileSettings);
      setNotice({ type: 'success', message: 'Profile details saved.' });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || 'Failed to save profile details.' });
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async () => {
    if (!token) return;
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      setNotice({ type: 'error', message: 'Current and new password required.' });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setNotice({ type: 'error', message: 'New password confirmation does not match.' });
      return;
    }

    setSavingPassword(true);
    setNotice({ type: '', message: '' });
    try {
      await adminApi.changePassword(passwordForm.currentPassword, passwordForm.newPassword, token);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setNotice({ type: 'success', message: 'Password updated successfully.' });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || 'Failed to update password.' });
    } finally {
      setSavingPassword(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Card
        sx={{
          mb: 2.5,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(37,99,235,0.12) 0%, rgba(15,23,42,0.03) 100%)',
          boxShadow: '0 20px 44px rgba(15, 23, 42, 0.08)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ width: 56, height: 56, fontWeight: 800, bgcolor: 'primary.main' }}>
                {fullName.slice(0, 1).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {fullName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profileData?.email || 'No email available'}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip icon={<Shield />} label={(profileData?.role || 'user').toUpperCase()} size="small" color="primary" />
                  <Chip icon={<CheckCircle />} label={subscriptionStatus.label} size="small" color={subscriptionStatus.color} />
                  {profileData?.company_name ? <Chip icon={<Apartment />} label={profileData.company_name} size="small" variant="outlined" /> : null}
                </Stack>
              </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveProfile}
                disabled={savingProfile}
              >
                {savingProfile ? 'Saving...' : 'Save Profile'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {notice.message && (
        <Alert severity={notice.type === 'error' ? 'error' : 'success'} sx={{ mb: 2.5, borderRadius: 3 }}>
          {notice.message}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <MiniStat title="Searches Today" value={usage.searches} helper={`${usage.searchLimit || 'No'} daily cap`} icon={<Search />} color="#2563eb" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MiniStat title="Emails Sent" value={usage.emails} helper={`${usage.emailLimit || 'No'} daily cap`} icon={<Email />} color="#10b981" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MiniStat title="SMTP Accounts" value={usage.smtpCount} helper={usage.smtpLimit > 0 ? `${usage.smtpLimit} limit` : 'Connected senders'} icon={<SettingsSuggest />} color="#8b5cf6" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MiniStat title="Replies / Opens" value={`${usage.totalReplied} / ${usage.totalOpened}`} helper={`${usage.totalClicked} clicks tracked`} icon={<MarkEmailRead />} color="#f59e0b" />
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
        <Grid item xs={12} lg={5}>
          <Card sx={{ borderRadius: 4, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                Account Overview
              </Typography>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Subscription
                  </Typography>
                  <Chip size="small" color={subscriptionStatus.color} label={subscriptionStatus.label} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Expires: {formatDate(profileData?.subscription_end_date)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Billing: Rs. {Number(profileData?.subscription_price || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Days remaining: {subscriptionStatus.days}
                </Typography>
              </Paper>

              <Stack spacing={1.5}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                    <Typography variant="body2" fontWeight={600}>Search quota</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {usage.searches} / {usage.searchLimit || 'Unlimited'}
                    </Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={usage.searchPercent} sx={{ height: 9, borderRadius: 999 }} />
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                    <Typography variant="body2" fontWeight={600}>Email quota</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {usage.emails} / {usage.emailLimit || 'Unlimited'}
                    </Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={usage.emailPercent} color="success" sx={{ height: 9, borderRadius: 999 }} />
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Member Since</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatDate(profileData?.created_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Last Activity</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatDate(profileData?.session_last_activity || profileData?.last_activity)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Total Sent Messages</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{usage.totalSentMessages}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Tracked Clicks</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{usage.totalClicked}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                Business Profile
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={profileForm.first_name}
                    onChange={(event) => setProfileForm((prev) => ({ ...prev, first_name: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={profileForm.last_name}
                    onChange={(event) => setProfileForm((prev) => ({ ...prev, last_name: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={profileForm.company_name}
                    onChange={(event) => setProfileForm((prev) => ({ ...prev, company_name: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Footer Sender Name"
                    value={footerForm.footerSenderName}
                    onChange={(event) => setFooterForm((prev) => ({ ...prev, footerSenderName: event.target.value }))}
                    helperText="Ye name email footer/signature me use ho sakta hai."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Footer Company"
                    value={footerForm.footerCompanyName}
                    onChange={(event) => setFooterForm((prev) => ({ ...prev, footerCompanyName: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Footer Email"
                    value={footerForm.footerEmail}
                    onChange={(event) => setFooterForm((prev) => ({ ...prev, footerEmail: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Footer Phone"
                    value={footerForm.footerPhone}
                    onChange={(event) => setFooterForm((prev) => ({ ...prev, footerPhone: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Business Address"
                    value={footerForm.footerAddress}
                    onChange={(event) => setFooterForm((prev) => ({ ...prev, footerAddress: event.target.value }))}
                    helperText="Footer, signature, ya template personalization ke liye."
                  />
                </Grid>
              </Grid>

              <Paper
                variant="outlined"
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 3,
                  bgcolor: darkMode ? alpha('#fff', 0.02) : '#fafcff',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Footer Preview
                </Typography>
                <Typography variant="body2">{footerForm.footerSenderName || fullName}</Typography>
                <Typography variant="body2">{footerForm.footerCompanyName || profileForm.company_name || 'Company name'}</Typography>
                <Typography variant="body2">{footerForm.footerAddress || 'Business address'}</Typography>
                <Typography variant="body2">{footerForm.footerEmail || profileData?.email || 'email@example.com'}</Typography>
                <Typography variant="body2">{footerForm.footerPhone || 'Phone number'}</Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                App Preferences
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="default-home-tab-label">Default Home Tab</InputLabel>
                    <Select
                      labelId="default-home-tab-label"
                      value={settings.defaultHomeTab}
                      label="Default Home Tab"
                      onChange={(event) => updateSetting('defaultHomeTab', event.target.value)}
                    >
                      {HOME_TAB_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3, height: '100%' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Theme</Typography>
                        <Typography variant="body2" color="text.secondary">Dark / light appearance</Typography>
                      </Box>
                      <Switch checked={darkMode} onChange={onToggleDarkMode} />
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
                    <FormControlLabel
                      control={<Switch checked={settings.autoReturnToSearchAfterQuickEmail} onChange={(event) => updateSetting('autoReturnToSearchAfterQuickEmail', event.target.checked)} />}
                      label="Auto return to Carrier Search after quick email"
                      sx={{ m: 0 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
                    <FormControlLabel
                      control={<Switch checked={settings.compactAccounts} onChange={(event) => updateSetting('compactAccounts', event.target.checked)} />}
                      label="Compact accounts view"
                      sx={{ m: 0 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
                    <FormControlLabel
                      control={<Switch checked={settings.inboxAutoRefresh} onChange={(event) => updateSetting('inboxAutoRefresh', event.target.checked)} />}
                      label="Prefer live inbox refresh"
                      sx={{ m: 0 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
                    <FormControlLabel
                      control={<Switch checked={settings.showExpiryAlerts} onChange={(event) => updateSetting('showExpiryAlerts', event.target.checked)} />}
                      label="Show expiry alerts"
                      sx={{ m: 0 }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Key color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Security
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  type={showPasswords.current ? 'text' : 'password'}
                  label="Current Password"
                  value={passwordForm.currentPassword}
                  onChange={(event) => setPasswordForm((prev) => ({ ...prev, currentPassword: event.target.value }))}
                  InputProps={{
                    endAdornment: (
                      <Button
                        size="small"
                        onClick={() => setShowPasswords((prev) => ({ ...prev, current: !prev.current }))}
                        sx={{ minWidth: 'auto', px: 1, color: 'text.secondary' }}
                      >
                        {showPasswords.current ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </Button>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  type={showPasswords.next ? 'text' : 'password'}
                  label="New Password"
                  value={passwordForm.newPassword}
                  onChange={(event) => setPasswordForm((prev) => ({ ...prev, newPassword: event.target.value }))}
                  helperText="At least 8 characters"
                  InputProps={{
                    endAdornment: (
                      <Button
                        size="small"
                        onClick={() => setShowPasswords((prev) => ({ ...prev, next: !prev.next }))}
                        sx={{ minWidth: 'auto', px: 1, color: 'text.secondary' }}
                      >
                        {showPasswords.next ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </Button>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  type={showPasswords.confirm ? 'text' : 'password'}
                  label="Confirm New Password"
                  value={passwordForm.confirmPassword}
                  onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                  InputProps={{
                    endAdornment: (
                      <Button
                        size="small"
                        onClick={() => setShowPasswords((prev) => ({ ...prev, confirm: !prev.confirm }))}
                        sx={{ minWidth: 'auto', px: 1, color: 'text.secondary' }}
                      >
                        {showPasswords.confirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </Button>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<Key />}
                  onClick={handlePasswordSave}
                  disabled={savingPassword}
                >
                  {savingPassword ? 'Updating...' : 'Change Password'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
